import React, { useState } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import MenuBar from "./MenuBar";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Sword, Users, Siren, Menu
} from "lucide-react";
import { usePreferences } from "../../context/PreferencesContext";

// Curated shortcuts for the busiest sections; "More" opens the full
// horizontal nav (all 19 sections) as a mobile overlay instead of a
// 5th fixed page, so the two navs are intentionally linked rather than
// covering disjoint, incidental subsets.
const bottomNavItems = [
  { path:"/",         icon:LayoutDashboard, label:{en:"Command",   mr:"कमांड"} },
  { path:"/command",  icon:Sword,           label:{en:"War Room",  mr:"वॉर रूम"} },
  { path:"/crowd-intelligence", icon:Users, label:{en:"Crowd",     mr:"गर्दी"} },
  { path:"/emergency",icon:Siren,           label:{en:"Emergency", mr:"आपत्कालीन"} },
];
const MORE_LABEL = { en: "More", mr: "अधिक" };

export default function DashboardLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { lang } = usePreferences();

  return (
    <div className="min-h-screen bg-surface">
      <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {/* Desktop primary nav — fixed below the header, so it needs its own
          top offset here rather than being part of the in-flow layout. */}
      <div className="hidden lg:block fixed top-[93px] left-0 right-0 z-30">
        <MenuBar />
      </div>

      {/* Mobile/tablet drawer — triggered by the header's hamburger and the
          bottom nav's "More" button. */}
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />

      {/* Main content area — offset for the fixed header (64px, 93px on lg)
          plus the horizontal MenuBar (44px) on lg+. */}
      {/* pt on desktop clears the fixed header (93px) plus the MenuBar beneath
          it. The bar grew from 44px to 52px when the menu type was enlarged,
          so this moved 137px -> 145px. Change both together or the first card
          slides under the nav. */}
      <main className="pt-16 lg:pt-[145px] pb-20 lg:pb-4 min-h-screen transition-all duration-200">
        <div className="p-4 md:p-6 max-w-screen-2xl mx-auto">
          {children}
        </div>
      </main>

      {/* Mobile bottom navigation — the exact same `gov-gradient-chrome` band
          as the header and the desktop MenuBar (not just a similar shade),
          so all three read as one continuous gold system regardless of
          screen width. */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 lg:hidden gov-gradient-chrome border-t border-saffron-dark flex shadow-nav">
        {bottomNavItems.map(item => {
          const Icon   = item.icon;
          const active = location.pathname === item.path;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={`flex-1 flex flex-col items-center justify-center py-2.5 gap-0.5 text-[10px] font-semibold transition-colors relative ${
                active ? "text-white" : "text-white/85"
              }`}
            >
              <Icon size={20} />
              <span>{item.label[lang]}</span>
              {active && <span className="absolute bottom-0 w-6 h-0.5 bg-white rounded-full" />}
            </NavLink>
          );
        })}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className={`flex-1 flex flex-col items-center justify-center py-2.5 gap-0.5 text-[10px] font-semibold transition-colors relative ${
            sidebarOpen ? "text-white" : "text-white/85"
          }`}
        >
          <Menu size={20} />
          <span>{MORE_LABEL[lang]}</span>
          {sidebarOpen && <span className="absolute bottom-0 w-6 h-0.5 bg-white rounded-full" />}
        </button>
      </nav>
    </div>
  );
}
